import { useId, useState } from 'react';
import { Copy, Frame } from 'lucide-react';
import { frameRateLabel, formatDuration, formatsOf, sequenceDuration, type SequenceDocument } from '@prism/contracts';
import { When } from '../../lib/format';
import { FormatDialog } from './FormatDialog';
import { formatColor } from './edit-formats';
import type { EditorApply } from './formatContext';

/**
 * The Details side tab: the sequence's description (autosaved with the sequence) and read-only facts about it; 1.7.0:
 * its formats next to the primary format, and Format… (the Format dialog, with the edit lock).
 */
export function SequenceDetails({ doc, description, onDescription, canEdit, owner, createdAt, updatedAt, updatedBy, apply, seal, onDuplicate }: {
  doc: SequenceDocument;
  description: string;
  onDescription: (v: string) => void;
  canEdit: boolean;
  owner: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  /** 1.7.0: the editor's edit function, for the Format dialog; without it there is no Format… button. */
  apply?: EditorApply;
  seal?: () => void;
  /** 1.7.4: Duplicate the sequence (and open the copy); without it there is no Duplicate button. */
  onDuplicate?: () => void;
}) {
  const id = useId();
  const [formatsOpen, setFormatsOpen] = useState(false);
  const others = formatsOf(doc).slice(1);
  return (
    <div className="sequence-details" data-testid="sequence-details">
      <div className="field">
        <label htmlFor={id}>Description</label>
        <textarea id={id} className="textarea" rows={5} value={description} onChange={(e) => onDescription(e.target.value)} disabled={!canEdit} maxLength={2000}
          placeholder={canEdit ? 'Add a description' : 'No description'} data-testid="sequence-description" />
        <span className="help">Shown in the Create list and searched there · {description.length}/2000</span>
      </div>
      <dl className="kv" data-testid="sequence-info">
        <dt>Format</dt>
        <dd>
          <span data-testid="sequence-info-format">{doc.width}×{doc.height}</span>
          {apply && (
            <button type="button" className="btn ghost sm" style={{ marginLeft: 8 }} onClick={() => setFormatsOpen(true)} data-testid="open-format-dialog"
              title={canEdit ? 'Change the size, add vertical or square formats' : 'See the formats (start editing to change them)'}><Frame size={12} /> Format…</button>
          )}
        </dd>
        {others.length > 0 && (
          <>
            <dt>Other formats</dt>
            <dd data-testid="sequence-info-formats">
              {others.map((f) => (
                <span key={f.id} className="format-chip" data-testid="sequence-info-format-item" data-format={f.id}>
                  <span className="format-swatch" style={{ background: formatColor(f.index) }} aria-hidden />{f.label} · {f.width}×{f.height}
                </span>
              ))}
            </dd>
          </>
        )}
        <dt>Frame rate</dt><dd>{frameRateLabel(doc.frameRate)} fps</dd>
        <dt>Duration</dt><dd className="mono">{formatDuration(sequenceDuration(doc))}</dd>
        <dt>Owner</dt><dd>{owner ?? '—'}</dd>
        <dt>Created</dt><When as="dd" iso={createdAt} />
        <dt>Updated</dt><dd><When iso={updatedAt} />{updatedBy ? ` · ${updatedBy}` : ''}</dd>
      </dl>
      {onDuplicate && (
        <button type="button" className="btn ghost sm" onClick={onDuplicate} data-testid="details-duplicate" title="Make a copy of the saved sequence and open it (versions and exports stay with this one)" style={{ alignSelf: 'flex-start' }}>
          <Copy size={12} /> Duplicate sequence
        </button>
      )}
      {apply && <FormatDialog doc={doc} open={formatsOpen} onClose={() => setFormatsOpen(false)} apply={apply} canEdit={canEdit} seal={seal} />}
    </div>
  );
}
